<template>
	<div class="cards-selector">
		<div class="cards-carousel">
			<div class="card-container" :style="{ '--card-color': cardsStore.availableColors[currentCard.color] }">

				<Carousel id="gallery-cards" ref="gallery-cards" :items-to-show="1" snap-align="center" :wrap-around="cards.length > 1" :breakpoints="breakpoints" v-model="cardIndex" @slide-start="slideStart" @slide-end="slideEnd">
					<Slide v-for="card in cards" :key="card.card_id">
						<CardViewer class="card-viewer-container" :class="{active: card.card_id === cardsStore.activeCardId}" :card-id="card.card_id" :last-four-digits="card.last4 ? card.last4 : '0000'" :label="card.name" :color="card.color" :frozen="!card.active" />
					</Slide>
				</Carousel>

			</div>
		</div>
		<div class="card-actions">
			<AppButton :text="languageStore.getString(cardsStore.stripe.detailsCardId ? 'hide_details' : 'details')" :loading="loadingAction" :disabled="!currentCard.active || currentCard.type === 'physical'" outline @click="showDetails" />
			<AppButton :text="languageStore.getString(currentCard.active ? 'freeze' : 'unfreeze')" :loading="loadingAction" outline @click="toggleFreeze" />
			<AppButton :text="languageStore.getString('settings')" outline @click="openSettings" />
		</div>
		<AppBottomSheet id="card-settings-bottom-sheet" :forceWidth="bottomSheetWidth" :active="settingsSheetOpen" @toggling-popup="togglingPopup">
			<CardSettings :card-id="currentCard.card_id || null" @toggling-popup="togglingPopup" />
		</AppBottomSheet>
		<OtpPopup ref="otpPopup" @new-otp="handleOtpSubmitted" />
	</div>
</template>

<script>
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import {Carousel, Slide} from "vue3-carousel";
import CardViewer from "@/console-new/components/config/administration/cards/CardViewer.vue";
import AppButton from "@/shared/components/AppButton.vue";
import API from '@/shared/helpers/API.js';
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CardSettings from "@/console-new/components/config/administration/cards/CardSettings.vue";
import OtpPopup from "@/console-new/components/shared/OtpPopup.vue";

export default {
	components: {CardSettings, ModalHeader, AppBottomSheet, CardViewer, Slide, Carousel, AppButton, OtpPopup},
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {
			cardsStore,
			languageStore,
			notificationStore,
			userStore
		}
	},
	mixins: [WindowWidth],
	name: "CardsSelector",
	data() {
		return {
			cardIndex: 0,
			loadingAction: false,
			settingsSheetOpen: false,
			pendingCardForDetails: null,
		}
	},
	mounted() {
		// Initialize cardIndex based on activeCardId
		if (this.cardsStore.activeCardId && this.cardsStore.businessCards) {
			const index = this.cardsStore.businessCards.findIndex(card => card.card_id === this.cardsStore.activeCardId);
			if (index !== -1) {
				this.cardIndex = index;
			}
		}
	},
	computed: {
		cards() {
			return this.cardsStore.businessCards ? this.cardsStore.businessCards : [];
		},
		currentCard() {
			return this.cards[this.cardIndex] || {};
		},
		breakpoints() {
			return {
				550: {itemsToShow: this.cards.length > 1 ? 1.4 : 1},
				630: {itemsToShow: this.cards.length > 1 ? 1.65 : 1},
				769: {itemsToShow: this.cards.length > 1 ? 1.95 : 1},
				920: {itemsToShow: this.cards.length > 1 ? 1.65 : 1},
				1025: {itemsToShow: this.cards.length > 1 ? 1 : 1},
				1350: {itemsToShow: this.cards.length > 1 ? 1.4 : 1},
				1500: {itemsToShow: this.cards.length > 1 ? 1.5 : 1},
				1700: {itemsToShow: this.cards.length > 1 ? 1.75 : 1},
			};
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1120) {
				return "720px";
			} else {
				return "1080px";
			}
		},
	},
	methods: {
		slideStart() {
			this.cardsStore.isCarouselScrolling = true;
		},
		slideEnd() {
			this.cardsStore.isCarouselScrolling = false;

			const cardAtIndex = this.cardsStore.businessCards[this.cardIndex] || null;
			if (cardAtIndex && cardAtIndex.card_id !== this.cardsStore.activeCardId) {
				this.cardsStore.changeCard(cardAtIndex.card_id);
			}
		},
		togglingPopup(open) {
			if (!open) {
				this.settingsSheetOpen = false;
			}
		},
		toggleFreeze() {
			this.loadingAction = true;
			API.init().freezeCard(this.userStore.currBusinessId, this.currentCard.card_id, !this.currentCard.active, (response) => {
				this.loadingAction = false;
				if(response && response.result === "OK") {
					this.currentCard.active = response.card_active;
					this.cardsStore.hideCardDetails();
					// Trigger refresh of chart and table data after 2s timeout to wait for webhook
					setTimeout(() => {
						this.cardsStore.triggerRefresh();
					}, 2000);
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		},
		openSettings() {
			this.settingsSheetOpen = true;
			this.cardsStore.hideCardDetails();
		},
		showDetails() {
			if(this.cardsStore.stripe.detailsCardId) {
				this.cardsStore.hideCardDetails();
			} else {
				// Store the card data and show OTP popup
				this.pendingCardForDetails = this.currentCard;
				this.$refs.otpPopup.askOtp();
			}
		},
		handleOtpSubmitted(otpCode) {
			if (!this.pendingCardForDetails) {
				return;
			}
			
			this.loadingAction = true;
			this.cardsStore.getStripeSecretKey(this.pendingCardForDetails, otpCode, (key, error) => {
				this.loadingAction = false;
				if(!key) {
					if (error && error.includes('invalid_otp')) {
						this.notificationStore.showNotification("invalid_otp");
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				}
				this.pendingCardForDetails = null;
			});
		}
	},
	watch: {
		'cardsStore.activeCardId': function (newVal) {
			if (newVal && this.cardsStore.businessCards.length > 0) {
				const index = this.cardsStore.businessCards.findIndex(card => card.card_id === newVal);
				if (index !== -1 && index !== this.cardIndex) {
					this.cardIndex = index;
				}
			}
		},
	},
}
</script>

<style scoped lang="scss">
#gallery-cards {
	width: 100%;
	height: 100%;
}
.cards-selector {
	width: 100%;

	.cards-carousel {
		width: 100%;

		.card-container {
			border-radius: 13px;
			box-shadow: -2px -2px 10px 0px var(--color-box-shadow-2) inset, 2px 2px 20px 0px var(--color-box-shadow-2) inset;
			min-height: 270px;
			display: flex;
			justify-content: center;
			align-items: center;
			position: relative;
			--card-color: #0752A3;

			&:after {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				border-radius: 13px;
				opacity: 0.15;
				background-color: var(--card-color);
				transition: opacity 0.3s ease-in-out, background-color 0.3s ease-in-out;
				pointer-events: none;
			}

			&.dark {
				&:after {
					opacity: 0.5;
				}
			}

			.card-viewer-container {
				position: relative;
				z-index: 1;
				padding: 20px;
				opacity: 0.3;
				transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out, filter 0.3s ease-in-out;
				transform: scale(0.9);
				filter: blur(6px);

				&.active {
					opacity: 1;
					transform: scale(1);
					filter: blur(0);
				}
			}
		}
	}

	.card-actions {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 10px;
		margin-top: 20px;
		height: 40px;

		.base-button {
			height: 100%;
			flex: 1;
		}
	}

}

</style>